import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ApiLink } from '../../../APILink/ApiLink';
import authHeader from '../../../Auth/authHeader';
import { errorAlert } from '../../../Components/Alerts';
import { FiscalYearContext } from '../../../FiscalYear/FiscalYearContext';
import './NewChart.scss';

const formatCompactCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value || 0);
};

const SERIES_META = {
  sales: { label: 'Shitjet', color: '#2f7d6b' },
  purchases: { label: 'Blerjet', color: '#2f6fb2' },
  expenses: { label: 'Shpenzimet', color: '#c2415a' },
  investments: { label: 'Investimet', color: '#b7791f' },
};

const MONTH_NAMES = {
  1: 'Janar',
  2: 'Shkurt',
  3: 'Mars',
  4: 'Prill',
  5: 'Maj',
  6: 'Qershor',
  7: 'Korrik',
  8: 'Gusht',
  9: 'Shtator',
  10: 'Tetor',
  11: 'Nentor',
  12: 'Dhjetor',
};

const formatMonthWithNumberAndText = (monthValue) => {
  const monthNumber = Number(monthValue);

  if (!Number.isNaN(monthNumber) && MONTH_NAMES[monthNumber]) {
    return `${monthNumber} - ${MONTH_NAMES[monthNumber]}`;
  }

  return String(monthValue);
};

const renderTooltipContent = ({ active, label, payload }) => {
  if (!active || !Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  const uniquePayload = payload.filter((entry, index, collection) => {
    return index === collection.findIndex((item) => item.dataKey === entry.dataKey);
  });

  return (
    <div className="new-chart-card__tooltip">
      <div className="new-chart-card__tooltip-header">
        <span className="new-chart-card__tooltip-kicker">Raporti Mujor</span>
        <div className="new-chart-card__tooltip-label">Muaji {formatMonthWithNumberAndText(label)}</div>
      </div>
      {uniquePayload.map((entry) => {
        const series = SERIES_META[entry.dataKey] || {
          label: entry.name,
          color: entry.color,
        };

        return (
        <div
          key={entry.dataKey}
          className="new-chart-card__tooltip-row"
          style={{ borderLeftColor: series.color }}
        >
          <div className="new-chart-card__tooltip-series">
            <span
              className="new-chart-card__tooltip-dot"
              style={{ backgroundColor: series.color }}
            />
            <span className="new-chart-card__tooltip-name">{series.label}</span>
          </div>
          <strong className="new-chart-card__tooltip-value">€ {Number(entry.value || 0).toLocaleString('en-US')}</strong>
        </div>
      )})}
    </div>
  );
};

export default function NewChart() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSeries, setActiveSeries] = useState({
    sales: true,
    purchases: true,
    expenses: true,
    investments: true,
  });
  const { fiscalYear } = useContext(FiscalYearContext);

  const seriesConfig = [
    { key: 'sales', ...SERIES_META.sales, type: 'area' },
    { key: 'purchases', ...SERIES_META.purchases, type: 'area' },
    { key: 'expenses', ...SERIES_META.expenses, type: 'line' },
    { key: 'investments', ...SERIES_META.investments, type: 'line' },
  ];

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          ApiLink.API_URL + 'Dashboard/detailsForChart',
          {
            headers: authHeader(),
            params: { fiscalYear },
          }
        );

        const mappedData = Array.isArray(response.data)
          ? response.data.map((item) => ({
              month: item.Muaji,
              sales: Number(item['Shitjet Mujore']) || 0,
              purchases: Number(item['Blerjet Mujore']) || 0,
              expenses: Number(item['Shpenzimet Mujore']) || 0,
              investments: Number(item['Investimet Mujore']) || 0,
            }))
          : [];

        setData(mappedData);
      } catch (error) {
        errorAlert('Gabim në kërkesë:', error);

        if (error.response?.data?.errorMessage) {
          errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
        } else if (error.message) {
          errorAlert(`Gabim: ${error.message}`);
        } else {
          errorAlert('Gabim i panjohur në kërkesë. Shih konsolën për detaje.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [fiscalYear]);

  const toggleSeries = (seriesKey) => {
    setActiveSeries((current) => ({
      ...current,
      [seriesKey]: !current[seriesKey],
    }));
  };

  return (
    <section className="new-chart-card">
      <div className="new-chart-card__header">
        <div>
          <h3 className="new-chart-card__title">Performanca Mujore Financiare</h3>
          <p className="new-chart-card__subtitle">Shitje, blerje, shpenzime dhe investime nga viti fiskal aktiv</p>
        </div>

        <div className="new-chart-card__controls">
          <span className="new-chart-card__controls-label">Kliko për të fshehur ose shfaqur seritë</span>

          <div className="new-chart-card__legend">
          {seriesConfig.map((series) => (
            <button
              key={series.key}
              type="button"
              className={`new-chart-card__legend-item ${activeSeries[series.key] ? 'is-active' : 'is-muted'}`}
              onClick={() => toggleSeries(series.key)}
              aria-pressed={activeSeries[series.key]}
              title={`${activeSeries[series.key] ? 'Fsheh' : 'Shfaq'} ${series.label}`}
            >
              <span className={`new-chart-card__legend-dot new-chart-card__legend-dot--${series.key}`} />
              <span>{series.label}</span>
            </button>
          ))}
          </div>
        </div>
      </div>

      <div className="new-chart-card__body">
        {loading ? (
          <div className="new-chart-card__skeleton" />
        ) : data.length === 0 ? (
          <div className="new-chart-card__empty">
            <span>Nuk ka të dhëna për grafik në vitin fiskal të zgjedhur.</span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 12, right: 8, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="newChartSales" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2f7d6b" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#2f7d6b" stopOpacity={0.01} />
                </linearGradient>
                <linearGradient id="newChartPurchases" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2f6fb2" stopOpacity={0.16} />
                  <stop offset="95%" stopColor="#2f6fb2" stopOpacity={0.01} />
                </linearGradient>
                <linearGradient id="newChartExpenses" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#c2415a" stopOpacity={0.14} />
                  <stop offset="95%" stopColor="#c2415a" stopOpacity={0.01} />
                </linearGradient>
                <linearGradient id="newChartInvestments" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#b7791f" stopOpacity={0.14} />
                  <stop offset="95%" stopColor="#b7791f" stopOpacity={0.01} />
                </linearGradient>
              </defs>

              <CartesianGrid vertical={false} stroke="var(--dashboard-chart-grid)" strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'var(--dashboard-chart-axis)', fontSize: 12 }}
                dy={8}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'var(--dashboard-chart-axis)', fontSize: 12 }}
                tickFormatter={(value) => `€ ${formatCompactCurrency(value)}`}
              />
              <Tooltip
                content={renderTooltipContent}
                contentStyle={{
                  borderRadius: '12px',
                  border: '1px solid #d7e3de',
                  boxShadow: '0 12px 30px rgba(40, 73, 69, 0.10)',
                }}
              />

              {activeSeries.sales ? (
                <>
                  <Area
                    type="monotone"
                    dataKey="sales"
                    name="Shitjet"
                    stroke="none"
                    fillOpacity={1}
                    fill="url(#newChartSales)"
                  />
                  <Line
                    type="monotone"
                    dataKey="sales"
                    name="Shitjet"
                    stroke="#2f7d6b"
                    strokeWidth={2.1}
                    dot={{ r: 2, strokeWidth: 0, fill: '#2f7d6b' }}
                    activeDot={{ r: 4.5 }}
                    connectNulls
                    isAnimationActive={false}
                  />
                </>
              ) : null}

              {activeSeries.purchases ? (
                <>
                  <Area
                    type="monotone"
                    dataKey="purchases"
                    name="Blerjet"
                    stroke="none"
                    fillOpacity={1}
                    fill="url(#newChartPurchases)"
                  />
                  <Line
                    type="monotone"
                    dataKey="purchases"
                    name="Blerjet"
                    stroke="#2f6fb2"
                    strokeWidth={2.1}
                    dot={{ r: 2, strokeWidth: 0, fill: '#2f6fb2' }}
                    activeDot={{ r: 4.5 }}
                    connectNulls
                    isAnimationActive={false}
                  />
                </>
              ) : null}

              {activeSeries.expenses ? (
                <>
                  <Area
                    type="monotone"
                    dataKey="expenses"
                    name="Shpenzimet"
                    stroke="none"
                    fillOpacity={1}
                    fill="url(#newChartExpenses)"
                  />
                  <Line
                    type="monotone"
                    dataKey="expenses"
                    name="Shpenzimet"
                    stroke="#c2415a"
                    strokeWidth={2.1}
                    dot={{ r: 2, strokeWidth: 0, fill: '#c2415a' }}
                    activeDot={{ r: 4.5 }}
                    connectNulls
                    isAnimationActive={false}
                  />
                </>
              ) : null}

              {activeSeries.investments ? (
                <>
                  <Area
                    type="monotone"
                    dataKey="investments"
                    name="Investimet"
                    stroke="none"
                    fillOpacity={1}
                    fill="url(#newChartInvestments)"
                  />
                  <Line
                    type="monotone"
                    dataKey="investments"
                    name="Investimet"
                    stroke="#b7791f"
                    strokeWidth={2.1}
                    dot={{ r: 2, strokeWidth: 0, fill: '#b7791f' }}
                    activeDot={{ r: 4.5 }}
                    connectNulls
                    isAnimationActive={false}
                  />
                </>
              ) : null}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}